import React, { useState } from 'react';
import { ArrowRight, Check, DollarSign, Euro, CircleDollarSign } from 'lucide-react';
import { mockData } from '../utils/mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';

export const PricingSection = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('EUR');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    childAge: '',
    clubName: ''
  });

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(mockData.contact.whatsappMessage);
    window.open(`https://wa.me/${mockData.contact.whatsappNumber}?text=${message}`, '_blank');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    // Aquí se integrará con el backend más adelante
    alert('¡Gracias! Te contactaremos pronto.');
    setIsOpen(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      childAge: '',
      clubName: ''
    });
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const currencyData = {
    EUR: {
      amount: mockData.pricing.amount,
      symbol: '€',
      icon: Euro
    },
    USD: {
      amount: mockData.pricing.equivalents.USD,
      symbol: '$',
      icon: DollarSign
    },
    CLP: {
      amount: mockData.pricing.equivalents.CLP.toLocaleString('es-CL'),
      symbol: '$',
      icon: CircleDollarSign
    }
  };

  return (
    <section id="inversion" className="relative py-24 bg-gradient-to-b from-black to-zinc-900">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Inversión
          </h2>
          <p className="text-xl text-white/70">
            Una decisión que cambiará tu forma de acompañar la carrera de tu hijo/a
          </p>
        </div>

        {/* Pricing Card */}
        <div className="max-w-2xl mx-auto">
          <div className="relative p-8 sm:p-12 bg-gradient-to-br from-zinc-900 to-black border-2 border-orange-500/50 rounded-3xl shadow-2xl">
            {/* Best Value Badge */}
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <div className="px-6 py-2 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full shadow-lg">
                <span className="text-white font-semibold text-sm">Acceso Inmediato</span>
              </div>
            </div>

            {/* Currency Selector */}
            <div className="flex justify-center gap-2 mb-8 mt-4">
              {Object.keys(currencyData).map((currency) => {
                const CurrencyIcon = currencyData[currency].icon;
                return (
                  <button
                    key={currency}
                    onClick={() => setSelectedCurrency(currency)}
                    className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all duration-300 ${
                      selectedCurrency === currency
                        ? 'bg-orange-500 text-white'
                        : 'bg-black/40 text-white/60 hover:bg-black/60 hover:text-white/90'
                    }`}
                  >
                    <CurrencyIcon className="w-4 h-4" />
                    <span className="text-sm font-medium">{currency}</span>
                  </button>
                );
              })}
            </div>

            {/* Price */}
            <div className="text-center mb-10">
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="text-5xl sm:text-6xl font-bold text-white">
                  {currencyData[selectedCurrency].symbol}
                  {currencyData[selectedCurrency].amount}
                </span>
              </div>
              <p className="text-white/60">Pago único - Acceso completo</p>
            </div>

            {/* Benefits */}
            <div className="space-y-4 mb-10">
              {mockData.pricing.benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3"
                >
                  <div className="flex-shrink-0 w-6 h-6 bg-orange-500/20 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-orange-500" />
                  </div>
                  <p className="text-white/90">{benefit}</p>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="space-y-4">
              <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogTrigger asChild>
                  <Button className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white text-lg py-6 rounded-xl shadow-lg hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105 group">
                    {mockData.pricing.cta}
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-white/10 text-white sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="text-2xl">Inscríbete en Familias 360</DialogTitle>
                    <DialogDescription className="text-white/70">
                      Completa tus datos y te contactaremos para finalizar tu inscripción
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="name" className="text-white/90">Nombre completo</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="bg-black/40 border-white/20 text-white mt-2"
                        placeholder="Tu nombre"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-white/90">Email</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="bg-black/40 border-white/20 text-white mt-2"
                        placeholder="tu@email.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-white/90">Teléfono</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="bg-black/40 border-white/20 text-white mt-2"
                        placeholder="+34 600 000 000"
                      />
                    </div>
                    <div>
                      <Label htmlFor="childAge" className="text-white/90">Edad del hijo/hija</Label>
                      <Input
                        id="childAge"
                        name="childAge"
                        type="number"
                        value={formData.childAge}
                        onChange={handleInputChange}
                        required
                        className="bg-black/40 border-white/20 text-white mt-2"
                        placeholder="Ej: 15"
                      />
                    </div>
                    <div>
                      <Label htmlFor="clubName" className="text-white/90">Club actual</Label>
                      <Input
                        id="clubName"
                        name="clubName"
                        value={formData.clubName}
                        onChange={handleInputChange}
                        required
                        className="bg-black/40 border-white/20 text-white mt-2"
                        placeholder="Nombre del club"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
                    >
                      Enviar solicitud
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              <button
                onClick={handleWhatsAppClick}
                className="w-full py-4 border-2 border-white/20 text-white rounded-xl hover:border-orange-500/50 hover:bg-orange-500/10 transition-all duration-300 font-semibold"
              >
                Contáctame por WhatsApp
              </button>
            </div>
          </div>
        </div>

        {/* Trust indicators */}
        <div className="mt-12 text-center">
          <p className="text-white/60 text-sm">
            Programa creado por un Agente FIFA con experiencia real en el sistema profesional
          </p>
        </div>
      </div>
    </section>
  );
};
